import { Component, OnDestroy, OnChanges, SimpleChanges, AfterViewInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs/Rx';
import { NavigationService } from '../../services/navigation.service';
import { MessageService } from 'base';
import { NetFacadeService } from '../../services/netfacade.service';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { ScheduledDiscovery } from 'DiscoveryEngine.TS/Discovery';
import {
	SchedDiscConfigBasicComponent, SchedDiscConfigProtocolsComponent,
	SchedDiscConfigScopesComponent, SchedDiscConfigAdvancedComponent,
	SchedDiscConfigScheduleComponent, SchedDiscConfigEventsComponent,
	SchedDiscConfigPrtgComponent, SchedDiscConfigAssetPandaComponent,
	SchedDiscConfigITGlueComponent, SchedDiscConfigHuduComponent,
	SchedDiscConfigJiraComponent, SchedDiscConfigHaloComponent,
} from 'discovery';
import { ChangeableContainer } from '../ChangeableContainer';
import { IdentityService } from '../../../identity';
import { ConfigurationService } from '../../../configuration';
import { IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DevicesComponent } from 'netview';
import { ReportsComponent } from 'infosets';


@Component({
    selector: 'discovery',
	templateUrl: './discovery.component.html'
})
export class DiscoveryComponent extends ChangeableContainer implements OnChanges, OnDestroy, AfterViewInit {

	public discovery: ScheduledDiscovery;
	private locationChangedSub: Subscription;
	private agent: AgentDto;
	private get integrationTable() { return this.configService.integrationTable; }
	public selectedTab: number;

	private onIntegrationSettingsLoadedSub: Subscription;

	@ViewChild(DevicesComponent)
	public devices: DevicesComponent;

	@ViewChild(ReportsComponent)
	public reports: ReportsComponent;

    @ViewChild(SchedDiscConfigBasicComponent)
	public basic: SchedDiscConfigBasicComponent;

    @ViewChild(SchedDiscConfigProtocolsComponent)
	public protocols: SchedDiscConfigProtocolsComponent;

    @ViewChild(SchedDiscConfigScopesComponent)
	public scopes: SchedDiscConfigScopesComponent;

    @ViewChild(SchedDiscConfigAdvancedComponent)
	public advanced: SchedDiscConfigAdvancedComponent;

    @ViewChild(SchedDiscConfigScheduleComponent)
	public schedule: SchedDiscConfigScheduleComponent;

    @ViewChild(SchedDiscConfigEventsComponent)
	public events: SchedDiscConfigEventsComponent;

    @ViewChild(SchedDiscConfigPrtgComponent)
	public prtg: SchedDiscConfigPrtgComponent;

	@ViewChild(SchedDiscConfigAssetPandaComponent)
	public assetpanda: SchedDiscConfigAssetPandaComponent;

	@ViewChild(SchedDiscConfigITGlueComponent)
	public itglue: SchedDiscConfigITGlueComponent;

	@ViewChild(SchedDiscConfigHaloComponent)
	public halo: SchedDiscConfigHaloComponent;

	@ViewChild(SchedDiscConfigHuduComponent)
	public hudu: SchedDiscConfigHuduComponent;

	@ViewChild(SchedDiscConfigJiraComponent)
	public jira: SchedDiscConfigJiraComponent;

	public readonly DevicesTabIndex = 0;
	public readonly ReportsTabIndex = 2;
	public readonly EventsTabIndex = 3;
	public readonly StatusTabIndex = 4;

	constructor(
		navService: NavigationService,
		messageService: MessageService,
		private configService: ConfigurationService,
		private netFacadeService: NetFacadeService,
		private identityService: IdentityService,
		private changeDetector: ChangeDetectorRef) {

		super(navService, messageService);

		this.selectedTab = 0;

		if (!this.configService.hasIntegrationTable) {
			this.onIntegrationSettingsLoadedSub = this.configService.onIntegrationTableLoaded.subscribe(
				() => {
					this.changeDetector.detectChanges();
					this.setChildren();
				},
				() => {
					this.messageService.displayErrorMessage("Failed to retrieve integration table.");
				});
		}

		this.getCurrentDiscovery();

		this.locationChangedSub = this.navService.onLocationChanged.subscribe(() => {
			this.getCurrentDiscovery();
		});
	}

	public get hasIntegrationTable(): boolean {
		return (!!this.integrationTable);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		return;
	}

	public ngAfterViewInit(): void {
		this.setChildren();
	}

	public ngOnDestroy(): void {
		if (this.locationChangedSub) {
			this.locationChangedSub.unsubscribe();
			this.locationChangedSub = undefined;
		}
		if (this.onIntegrationSettingsLoadedSub) {
			this.onIntegrationSettingsLoadedSub.unsubscribe();
			this.onIntegrationSettingsLoadedSub = undefined;
		}
	}

	public setChildren(): void {
		this.children = [
			this.basic,
			this.protocols,
			this.scopes,
			this.advanced,
			this.schedule,
			this.events,
			this.prtg,
			this.assetpanda,
			this.itglue,
			this.halo,
			this.hudu,
			this.jira,
		];
	}

	public isITGlueTabVisible(): boolean {
		if (this.hasIntegrationTable) {
			var itGlueIntegration = this.integrationTable.Find(IntegrationNames.ITGlue);
			if (itGlueIntegration == null)
				return false;
			return itGlueIntegration.Enabled;
		}
		return false;
	}

	public isHaloTabVisible(): boolean {
		if (this.hasIntegrationTable) {
			var haloIntegration = this.integrationTable.Find(IntegrationNames.Halo);
			if (haloIntegration == null)
				return false;
			return haloIntegration.Enabled;
		}
		return false;
	}

	public isHuduTabVisible(): boolean {
		if (this.hasIntegrationTable) {
			var huduIntegration = this.integrationTable.Find(IntegrationNames.Hudu);
			if (huduIntegration == null)
				return false;
			return huduIntegration.Enabled;
		}
		return false;
	}

	public isAssetPandaTabVisible(): boolean {
		if (this.hasIntegrationTable) {
			var assetPandaIntegration = this.integrationTable.Find(IntegrationNames.AssetPanda);
			if (assetPandaIntegration == null)
				return false;
			return assetPandaIntegration.Enabled;
		}
		return false;
	}

	public isJiraTabVisible(): boolean {
		if (this.hasIntegrationTable) {
			var jiraIntegration = this.integrationTable.Find(IntegrationNames.Jira);
			if (jiraIntegration == null)
				return false;
			return jiraIntegration.Enabled;
		}
		return false;
	}

	public get isLoggedIn(): boolean {
		return this.identityService.isLoggedIn;
	}

	private getCurrentDiscovery(): void {
		this.discovery = null;
		this.agent = null;

		if (this.navService.location.hasSchedDiscGuid) {
			let agentGuid = this.navService.location.agentGuid;
			this.agent = this.netFacadeService.findAgent(agentGuid);

			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDisc = this.agent.GetScheduledDiscovery(schedDiscGuid);
			if (schedDisc) {
				this.discovery = schedDisc;
			}
		}
	}

	public isSaveEnabled(): boolean {
		return (this.discovery && this.agent && (this.agent.HasChanges || this.tempChanges));
	}

	protected save(): void {
		this.messageService.displayInfoMessage(`Saving changes to discovery '${this.discovery.Name}'`);

		// Make local copy of the discovery and agent references because the
		// discovery and agent loaded in this component might change before 
		// the asynchronous save operation completes
		let discovery: ScheduledDiscovery = this.discovery;
		let agent: AgentDto = this.agent;
		this.netFacadeService.updateScheduledDiscovery(discovery, agent).subscribe(
			(result: ScheduledDiscovery) => {
				agent.HasChanges = false;
			},
			(error: any) => {
				this.messageService.displayErrorMessage("Saving discovery changes failed");
			});
	}

	public onTabChange(event: any): void {
		this.selectedTab = event.index;

		switch (this.selectedTab) {	
			case this.DevicesTabIndex:
				if (this.devices) {
					this.devices.reloadDeviceList();
				}
				break;
			case this.ReportsTabIndex:
				if (this.reports) {
					this.reports.reloadReport();
				}
				break;
		}
	}

}
